import {
	type Action,
	type BoundActions,
	createHook,
	createStore,
	type HookFunction,
} from 'react-sweet-state';

import { type AllTeamsQuery, teamsClient } from '@atlassian/teams-client';
import { type TeamMembership, type TeamMembershipSettings } from '@atlassian/teams-client/types';

import {
	acceptTeamInvitation,
	approveJoinRequest,
	declineJoinRequest,
	getMyTeamMembership,
	getRequestingToJoinUsers,
	getTeamHealth,
	getTeamMembers,
	getTeamProfile,
	inviteUsersToTeam,
	joinTeam,
	prefillTeamData,
	removeTeamMember,
} from './actions';
import getAllTeams from './actions/get-all-teams';
import type {
	StoreApi,
	TeamAgentsState,
	TeamAppState,
	TeamHealthState,
	TeamMembersState,
} from './types';

const initialState: TeamAppState = {
	teamProfile: {
		teamId: '',
		isLoading: true,
		hasLoaded: false,
		errorType: null,
		data: {},
	},
	teamHealth: {
		teamId: '',
		isLoading: true,
		hasLoaded: false,
		errorType: null,
		data: [],
	},
	teamMembers: {
		teamId: '',
		isLoading: true,
		isLoadingNextPage: false,
		hasLoaded: false,
		hasError: false,
		errorType: null,
		members: [],
		requestingToJoinUsers: [],
		membersPageInfo: {
			hasNextPage: false,
			cursor: null,
		},
		requestingToJoinUsersPageInfo: {
			hasNextPage: false,
			cursor: null,
		},
	},
	teamAgents: {
		teamId: '',
		isLoading: true,
		isLoadingNextPage: false,
		hasLoaded: false,
		hasError: false,
		errorType: null,
		members: [],
	},
	myTeamMembership: {
		teamId: '',
		hasLoaded: false,
		membership: undefined,
	},
	exitedTeams: new Set(),
	allTeamsData: {
		teams: [],
		isLoading: true,
		hasError: false,
		cursor: '',
	},
};

export type TeamActions = typeof actions;

const actions = {
	/**
	 * @deprecated set the config on the teamsClient using useTeamsClientSetup
	 */
	setTeamClientRootUrl:
		(rootUrl: string): Action<TeamAppState> =>
		() => {
			teamsClient.setLegionRootUrl(rootUrl);
		},
	collectTeamData:
		(teamId: string) =>
		async ({ dispatch, getState }: StoreApi) => {
			// If we already have the team's data, do not collect it again
			if (getState().teamProfile.teamId === teamId) {
				return;
			}

			await Promise.all([
				dispatch(getTeamProfile(teamId)),
				dispatch(getMyTeamMembership(teamId)),
				dispatch(getTeamMembers(teamId)),
				dispatch(getTeamHealth(teamId)),
			]);

			void dispatch(getRequestingToJoinUsers(teamId));
		},
	restoreTeam:
		(teamId: string, options?: { fetchTeamData?: boolean }) =>
		async ({ dispatch }: StoreApi) => {
			await teamsClient.restoreTeam(teamId);

			if (options?.fetchTeamData) {
				await Promise.all([
					dispatch(getTeamProfile(teamId)),
					dispatch(getMyTeamMembership(teamId)),
					dispatch(getTeamMembers(teamId)),
					dispatch(getTeamHealth(teamId)),
				]);
			}
		},
	fetchTeamMembersNextPage:
		(teamId: string, count: number = 100) =>
		async ({ dispatch, getState }: StoreApi) => {
			if (!getState().teamMembers.membersPageInfo.hasNextPage) {
				return;
			}

			await dispatch(getTeamMembers(teamId, true, count));
		},
	refreshRequestingToJoinUsers:
		(teamId: string) =>
		async ({ dispatch, getState }: StoreApi): Promise<TeamMembership[]> => {
			// Check that we're refreshing data for the current team
			if (getState().teamProfile.teamId !== teamId) {
				return [];
			}

			return await dispatch(getRequestingToJoinUsers(teamId));
		},

	prefillTeamData,

	updateTeamProfile:
		(
			teamId: string,
			data: {
				displayName?: string;
				description?: string;
				headerImageId?: string;
				membershipSettings?: TeamMembershipSettings;
			},
		) =>
		async ({ dispatch, getState, setState }: StoreApi) => {
			const isTransitioningToOpen =
				data.membershipSettings === 'OPEN' &&
				getState().teamProfile.data.membershipSettings === 'MEMBER_INVITE';

			const response = await teamsClient.updateTeamById(teamId, data);

			setState({
				teamProfile: {
					...getState().teamProfile,
					data: response,
				},
			});

			if (isTransitioningToOpen) {
				dispatch(getTeamMembers(teamId));
			}

			return response;
		},

	acceptAllJoinRequests:
		(teamId: string) =>
		async ({ dispatch, getState }: StoreApi) => {
			const requests = getState().teamMembers.requestingToJoinUsers;

			await Promise.all(
				requests.map((request) => {
					return dispatch(approveJoinRequest(teamId, request.membershipId.memberId, request.user!));
				}),
			);
		},

	declineAllJoinRequests:
		(teamId: string) =>
		async ({ dispatch, getState }: StoreApi) => {
			const requests = getState().teamMembers.requestingToJoinUsers;

			await Promise.all(
				requests.map((request) => {
					return dispatch(declineJoinRequest(teamId, request.membershipId.memberId));
				}),
			);
		},

	// Throws if we cannot remove the member
	removeTeamMember,

	revokeTeamInvite:
		(teamId: string, memberId: string) =>
		async ({ getState, setState }: StoreApi) => {
			const {
				teamMembers: { teamId: currentTeamId },
			} = getState();
			const removedInvite = await teamsClient.revokeTeamInvite(teamId, memberId);

			if (removedInvite && removedInvite.state !== 'ALUMNI') {
				throw Error('Invite claims to be cancelled but was not');
			}

			if (currentTeamId !== teamId) {
				return;
			}

			setState({
				teamMembers: {
					...getState().teamMembers,
					members: getState().teamMembers.members.filter(
						(member) => !!member.user && member.user.id !== memberId,
					),
				},
			});
		},

	// Throws if teamsClient.selfJoinTeam throws
	joinTeam,

	// Throws if teamsClient.cancelJoinRequest throws
	cancelJoinRequest:
		(teamId: string) =>
		async ({ getState, setState }: StoreApi) => {
			const {
				teamProfile: { teamId: currentTeamId },
			} = getState();
			await teamsClient.cancelJoinRequest(teamId);

			const state = getState();

			if (currentTeamId !== teamId) {
				return;
			}

			setState({
				...state,
				myTeamMembership: {
					teamId,
					hasLoaded: true,
					membership: undefined,
				},
			});
		},

	// Throws if teamsClient.acceptTeamInvitation throws
	acceptTeamInvitation,

	// Throws if teamsClient.declineTeamInvitation throws
	declineTeamInvitation:
		(teamId: string) =>
		async ({ getState }: StoreApi) => {
			const { exitedTeams } = getState();
			await teamsClient.declineTeamInvitation(teamId);

			exitedTeams.add(teamId);

			// No need to re-fetch, when an invite is declined we navigate away
		},

	// Throws if teamsClient.inviteUsersToTeam or publicApiClient.addUsersToTeam throws
	inviteUsersToTeam,

	// Throws if teamsClient.approveJoinRequest throws
	approveJoinRequest,

	// Throws if teamsClient.declineJoinRequest throws
	declineJoinRequest,

	// Throws if teamsClient.deleteTeam throws
	deleteTeam:
		(teamId: string) =>
		async ({ getState, setState }: StoreApi) => {
			const { exitedTeams } = getState();
			await teamsClient.deleteTeam(teamId);
			setState({
				teamProfile: initialState.teamProfile,
				teamMembers: initialState.teamMembers,
				myTeamMembership: initialState.myTeamMembership,
				teamHealth: initialState.teamHealth,
			});

			exitedTeams.add(teamId);
		},

	getAllTeams:
		(allTeamsQuery: AllTeamsQuery, keepExistingData?: boolean) =>
		async ({ dispatch }: StoreApi) => {
			await dispatch(getAllTeams(allTeamsQuery, keepExistingData));
		},

	refreshTeamHealthMonitors:
		(teamId: string | undefined) =>
		async ({ dispatch, getState }: StoreApi): Promise<void> => {
			// Check that we're refreshing data for the current team
			if (getState().teamProfile.teamId !== teamId) {
				return Promise.reject(`${teamId} is not the current team`);
			}

			return await dispatch(getTeamHealth(teamId));
		},
};

const TeamStore = createStore<TeamAppState, TeamActions>({
	initialState,
	actions,
});

/* Profile */
export const useTeamProfileDataState = createHook(TeamStore, {
	selector: (state) => state.teamProfile,
});

/* Members */
export const useTeamMembersState: HookFunction<
	TeamMembersState,
	BoundActions<TeamAppState, TeamActions>
> = createHook(TeamStore, {
	selector: (state) => state.teamMembers,
});

/* My membership */
export const useMyMembership = createHook(TeamStore, {
	selector: (state) => state.myTeamMembership,
});

/* Exited teams list */
export const useExitedTeams = createHook(TeamStore, {
	selector: (state) => state.exitedTeams,
});

/* All teams */
export const useAllTeams = createHook(TeamStore, {
	selector: (state) => state.allTeamsData,
});

/* Team health */
export const useTeamHealthState: HookFunction<
	TeamHealthState,
	BoundActions<TeamAppState, TeamActions>
> = createHook(TeamStore, {
	selector: (state) => state.teamHealth,
});

export const useTeamAgentsState: HookFunction<
	TeamAgentsState,
	BoundActions<TeamAppState, TeamActions>
> = createHook(TeamStore, {
	selector: (state) => state.teamAgents,
});
